import React, { useEffect, useState, useCallback } from "react";
import MetaTags from "react-meta-tags";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Button,
  Table,
  Alert
} from "reactstrap";
import Loader from "../../components/Common/Loader";
import Select from "react-select";
import {
  getAgent,
  getCampaignFullList,
  getGestionesContactoRead,
  getReportsForHourList
} from "../../helpers/backend";
import ModalManagementDetails from "../Whatsapp/components/ModalManagementDetails";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell
} from "recharts";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_green.css";
import "flatpickr/dist/l10n/es.js";
import "./Css/Report.css";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"];

const ReportForHour = () => {
  // Estados
  const [detallesModal, setDetallesModal] = useState(false);
  const [gestionDetallada, setGestionDetallada] = useState(null);
  const [filters, setFilters] = useState({
    fechaInicio: "",
    fechaFin: "",
    estado: "",
    agente: "",
    campaña: ""
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [showWarning, setShowWarning] = useState(false);
  const [selectedGestiones, setSelectedGestiones] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [agents, setAgents] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({
    agents: [],
    campaign: null,
    estado: null
  });

  // Obtener datos iniciales
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [agentsRes, campaignsRes] = await Promise.all([
          getAgent(),
          getCampaignFullList()
        ]);

        setAgents(agentsRes.data);
        setCampaigns(campaignsRes.data);
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };

    fetchInitialData();
  }, []);

  // Manejar cambios de fecha
  const handleDateChange = useCallback((selectedDates) => {
    if (selectedDates.length === 2) {
      const [start, end] = selectedDates;
      setSelectedDate(selectedDates);
      setFilters((prev) => ({
        ...prev,
        fechaInicio: start.toISOString().split("T")[0],
        fechaFin: end.toISOString().split("T")[0]
      }));
    }
  }, []);

  // Manejar cambios en los selects
  const handleSelectChange = useCallback((field, selectedOption) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [field]: selectedOption
    }));

    const value = Array.isArray(selectedOption)
      ? selectedOption.map((opt) => opt.value).join(",")
      : selectedOption?.value || "";

    setFilters((prev) => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Obtener reporte
  const getReportData = useCallback(async () => {
    if (!filters.fechaInicio || !filters.fechaFin) {
      setShowWarning(true);
      return;
    }

    setLoading(true);
    setSelectedGestiones([]);

    try {
      const params = new URLSearchParams({
        fecha_inicio: filters.fechaInicio,
        fecha_fin: filters.fechaFin,
        estado: filters.estado,
        agents: filters.agente,
        campaign: filters.campaña,
        type: "WhatsApp"
      });

      const response = await getReportsForHourList(`?${params.toString()}`);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching report data:", error);
    } finally {
      setLoading(false);
      setShowWarning(false);
    }
  }, [filters]);

  // Resetear búsqueda
  const resetSearch = useCallback(() => {
    setFilters({
      fechaInicio: "",
      fechaFin: "",
      estado: "",
      agente: "",
      campaña: ""
    });
    setSelectedDate(null);
    setSelectedOptions({
      agents: [],
      campaign: null,
      estado: null
    });
    setSelectedGestiones([]);
    setData({});
  }, []);

  // Formatear datos para el gráfico
  const formattedData = Object.entries(data)
    .filter(([key]) => key !== "total_gestiones")
    .map(([hour, value]) => ({
      hora: hour.replace("_", " "),
      gestiones: value.contador || 0,
      details: value.gestiones || []
    }));

  // Manejar clic en barra del gráfico
  const handleBarClick = (data) => {
    if (data?.activePayload?.[0]?.payload?.details) {
      setSelectedGestiones(data.activePayload[0].payload.details);
    }
  };

  // Manejar apertura de modal
  const handleGestionDetallada = async (id) => {
    try {
      const response = await getGestionesContactoRead(id);
      setGestionDetallada(response.data);
      setDetallesModal(true);
    } catch (error) {
      console.error("Error fetching gestion details:", error);
    }
  };

  return (
    <div className="page-content report-for-time-container">
      <MetaTags>
        <title>Cuidad Limpia | Reporte por Hora</title>
      </MetaTags>

      <Container fluid>
        {/* Encabezado */}
        <div className="report-header">
          <h2 className="report-title">Reporte por hora</h2>
          <p className="report-subtitle">
            Visualiza las gestiones realizadas por franja horaria
          </p>
        </div>

        {/* Modal de detalles */}
        {detallesModal && (
          <ModalManagementDetails
            isOpen={detallesModal}
            onCloseClick={() => setDetallesModal(false)}
            GestionDetallada={gestionDetallada}
          />
        )}

        {/* Panel de filtros */}
        <Card className="filter-card">
          <CardBody>
            <Row>
              <Col md={12} lg={3} className="mb-3">
                <label className="filter-label">Rango de fechas</label>
                <Flatpickr
                  className="form-control"
                  placeholder="Seleccione fechas"
                  options={{
                    mode: "range",
                    dateFormat: "Y-m-d",
                    locale: "es",
                    maxDate: new Date()
                  }}
                  onChange={handleDateChange}
                  value={selectedDate}
                />
              </Col>

              <Col md={12} lg={2} className="mb-3">
                <label className="filter-label">Estado</label>
                <Select
                  onChange={(opt) => handleSelectChange("estado", opt)}
                  options={[
                    { value: "", label: "Todos" },
                    { value: "EN_GESTION", label: "En Gestión" },
                    { value: "VENCIDO", label: "Vencido" },
                    { value: "FINALIZADO", label: "Finalizado" },
                    { value: "PENDIENTE", label: "Pendiente" }
                  ]}
                  placeholder="Estado"
                  value={selectedOptions.estado}
                  menuPosition="fixed"
                />
              </Col>

              <Col md={12} lg={3} className="mb-3">
                <label className="filter-label">Agentes</label>
                <Select
                  options={[
                    { label: "Todos", value: "" },
                    ...agents.map((agent) => ({
                      label: agent.get_full_name,
                      value: agent.id
                    }))
                  ]}
                  value={selectedOptions.agents}
                  onChange={(opt) => handleSelectChange("agente", opt)}
                  isMulti
                  placeholder="Seleccione agentes"
                  menuPosition="fixed"
                />
              </Col>

              <Col md={12} lg={2} className="mb-3">
                <label className="filter-label">Campañas</label>
                <Select
                  options={[
                    { label: "Todas", value: "" },
                    ...campaigns.map((campaign) => ({
                      label: campaign.name,
                      value: campaign.id
                    }))
                  ]}
                  onChange={(opt) => handleSelectChange("campaña", opt)}
                  value={selectedOptions.campaign}
                  menuPosition="fixed"
                  placeholder="Seleccione campaña"
                />
              </Col>

              <Col md={12} lg={2} className="d-flex align-items-end mb-3">
                <div className="d-flex w-100">
                  <Button
                    color="primary"
                    className="me-2 flex-grow-1"
                    onClick={getReportData}
                    disabled={loading}
                  >
                    {loading ? "Consultando..." : "Consultar"}
                  </Button>
                  <Button
                    outline
                    color="secondary"
                    className="flex-grow-1"
                    onClick={resetSearch}
                  >
                    Limpiar
                  </Button>
                </div>
              </Col>
            </Row>

            {showWarning && (
              <Alert color="warning" className="mt-2">
                Por favor, seleccione un rango de fechas para continuar.
              </Alert>
            )}
          </CardBody>
        </Card>

        {/* Resultados */}
        {loading ? (
          <Loader />
        ) : (
          Object.keys(data).length > 0 && (
            <Row className="mt-4">
              <Col lg={selectedGestiones.length > 0 ? 7 : 12}>
                <Card className="chart-card">
                  <CardBody>
                    <h5 className="chart-title">Gestiones por hora</h5>
                    <div className="chart-container">
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={formattedData} onClick={handleBarClick}>
                          <XAxis
                            dataKey="hora"
                            tick={{ fontSize: 12 }}
                            angle={-45}
                            textAnchor="end"
                            height={60}
                          />
                          <YAxis />
                          <Tooltip
                            contentStyle={{
                              borderRadius: "8px",
                              boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
                            }}
                          />
                          <Bar dataKey="gestiones">
                            {formattedData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              {selectedGestiones.length > 0 && (
                <Col lg={5}>
                  <Card className="details-card">
                    <CardBody>
                      <h5 className="details-title">
                        Detalles para la hora seleccionada
                        <span className="badge bg-primary ms-2">
                          {selectedGestiones.length} gestiones
                        </span>
                      </h5>
                      <div className="details-table-container">
                        <Table hover className="details-table">
                          <thead>
                            <tr>
                              <th>Contacto</th>
                              <th>Estado</th>
                              <th>Fecha</th>
                              {/* Eliminamos la columna de Acciones */}
                            </tr>
                          </thead>
                          <tbody>
                            {selectedGestiones.map((gestion) => (
                              <tr
                                key={gestion.id}
                                onClick={() =>
                                  handleGestionDetallada(gestion.id)
                                }
                                className="clickable-row"
                              >
                                <td>{gestion.contacto}</td>
                                <td>
                                  <span
                                    className={`status-badge ${gestion.estado
                                      .toLowerCase()
                                      .replace(" ", "-")}`}
                                  >
                                    {gestion.estado}
                                  </span>
                                </td>
                                <td>
                                  {new Date(
                                    gestion.created_date
                                  ).toLocaleString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              )}
            </Row>
          )
        )}
      </Container>
    </div>
  );
};

export default ReportForHour;
